import "./reviewPage.css";
import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star } from "lucide-react";
import dayjs from "dayjs";
import usersRequest from "../../request/usersRequest";
import scheduleBasedRequest from "../../request/scheduleBasedRequest"; // depends where submitReview lives
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import BannerMessage from "../common/bannerMessage/bannerMessage";

const ReviewPage = () => {
	const { bookingId } = useParams();
	const navigate = useNavigate();
	const { language, userData } = useContext(CombinedContext);

	const [loading, setLoading] = useState(true);
	const [booking, setBooking] = useState(null);

	const [rating, setRating] = useState(0);
	const [comment, setComment] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
	const [existingReview, setExistingReview] = useState(null);
	const [isEditMode, setIsEditMode] = useState(false);

	const MAX_CHARS = 250;

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);
	
		setTimeout(() => {
			setExiting(true);
		}, duration - 250);
	
		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	useEffect(() => {
		async function load() {
			try {
				const res = await usersRequest.getBooking(bookingId);
				setBooking(res.booking);
				if (res.review) {
					setExistingReview(res.review);
					setRating(res.review.rating);
					setComment(res.review.comment || "");
					setIsEditMode(true);
				}
			} catch (err) {
				console.error("Failed to load booking:", err);
			}
			setLoading(false);
		}
		load();
	}, [bookingId]);

	async function handleSubmit() {
		if (rating === 0) {
			showBanner(
				"error",
				translations[language].reviewPage?.errorNoRating ||
					"Please select a rating"
			);
			return;
		}
	
		setSubmitting(true);
	
		try {
			if (isEditMode) {
				// 🔜 you will replace this later
				await usersRequest.updateReview(
					existingReview.reviewId,
					rating,
					comment
				);
			} else {
				await usersRequest.createReview(bookingId, rating, comment);
			}
	
			navigate(`/booking/${bookingId}`);
		} catch (err) {
			console.error("Review submit failed:", err);
			showBanner(
				"error",
				translations[language].reviewPage?.errorSubmit ||
					"Failed to submit review"
			);
		} finally {
			setSubmitting(false);
		}
	}
	

	if (loading || !booking) return <div className="loading">Loading...</div>;

	return (
		<div className="sf-review-page">
			{/* HEADER */}
			<div className="sf-reviewPage-header">
				<button className="sf-review-back" onClick={() => navigate(-1)}>
					<ArrowLeft size={20} />
				</button>
				<div className="sf-reviewPage-header-title">
					{isEditMode
						? translations[language].reviewPage?.editTitle || "Edit your review"
						: translations[language].reviewPage?.title || "Leave a review"}
				</div>
			</div>

			{/* SHOP NAME */}
			<div className="sf-review-shop-name">{booking.shopName}</div>

			{/* DATE */}
			<div className="sf-review-date">{dayjs(booking.date).format("MMM D, YYYY")}</div>

			{/* STARS */}
			<div className="sf-stars">
				{[1, 2, 3, 4, 5].map((num) => (
					<span key={num} className={`sf-star ${rating >= num ? "active" : ""}`} onClick={() => setRating(num)}>
						<Star size={30} fill="currentColor" strokeWidth={0.5} />
					</span>
				))}
			</div>

			{/* COMMENT */}
			<div className="sf-review-comments">
				<textarea
					className="sf-review-textarea"
					placeholder={translations[language].reviewPage?.placeholder || "Share your experience..."}
					value={comment}
					maxLength={MAX_CHARS} // <-- Hard limit
					onChange={(e) => setComment(e.target.value)}
				/>

				<div className="sf-review-charcount">
					{comment.length}/{MAX_CHARS}
				</div>
			</div>

			{isEditMode && (
				<div className="sf-review-edit-hint">
					You’ve already reviewed this booking. You can update your review below.
				</div>
			)}


			{/* SUBMIT */}
			<button
				className="sf-review-submit"
				onClick={handleSubmit}
				disabled={submitting}
			>
				{submitting
					? translations[language].reviewPage?.submitting || "Saving..."
					: isEditMode
						? translations[language].reviewPage?.save || "Save changes"
						: translations[language].reviewPage?.submit || "Submit review"}
			</button>

			{banner && (
				<BannerMessage
					type={banner.type}
					text={banner.text}
					exiting={exiting}
				/>
			)}
		</div>
	);
};

export default ReviewPage;
